import React, { useMemo, useRef } from "react";
import { InlineMath } from "react-katex";

/**
 * Oś z wieloma przedziałami.
 * - lockPair: wymuś ustawienie DWÓCH skończonych końców w stałych pozycjach (pairFractions).
 * - autoLockTwoFinite: gdy wykryjemy dokładnie dwa skończone końce (np. union: (-∞,a)∪[b,∞)),
 *   automatycznie ustawiamy je w twoFiniteFractions, aby paski nie były „za krótkie”.
 * - Otwarte końce: kółko puste + „dziura” w wypełnieniu pod kółkiem.
 * - Grube poziome linie:
 *   • (a,b)/[a,b]/(a,b]/[a,b) – jedna linia między a i b,
 *   • półnieskończone – linia na zewnątrz.
 * - Pin pionowy przy otwartym końcu kończy się POD kółkiem (nie wchodzi do środka).
 *
 * Poprawka iOS:
 *  - ułamki z \\dfrac / \\frac renderujemy jako czyste SVG (licznik/kreska/mianownik)
 *    zamiast foreignObject (które potrafi się rozjechać na Safari iOS).
 */
export default function NumberLineRanges({
  ranges = [],
  width = 560,
  height = 170,
  color = "#f59e0b",
  showZero = true,
  layout = "single",

  // skala klasyczna
  margin = 3.2,
  marginMin = 1.0,
  marginMax = 4.5,
  minTickGapPx = 92,

  // stały układ dwóch liczb — manualny
  lockPair = false,
  pairFractions = [1 / 3, 2 / 3],

  // stały układ dwóch liczb — automatyczny (np. rozłączne półproste)
  autoLockTwoFinite = true,
  twoFiniteFractions = [0.25, 0.75],

  // podpisy / typografia
  fontSize = 18,
  fontFamily = "KaTeX_Main, STIXGeneral, 'Times New Roman', serif",
  useMathMinus = true,
}) {
  /* ── stałe rysunkowe ─────────────────────────────────────────────── */
  const AR = 10; // grot strzałki
  const pinR = 7; // promień kółek
  const strokeW = 3; // grubość pinów i kapów
  const halfW = strokeW / 2;

  /* ── helpers: minus + fraction svg ───────────────────────────────── */
  const normalizeMinus = (txt) =>
    useMathMinus ? String(txt ?? "").replace(/-/g, "−") : String(txt ?? "");

  // Czy latex to liczba prosta (np. "-4", "0", "6", "1,5")?
  const isSimpleNumericLatex = (latex) => {
    if (!latex) return false;
    const s = normalizeMinus(
      String(latex ?? "")
        .replace(/\\,/g, " ")
        .replace(/[{}]/g, "")
        .replace(/\s+/g, " ")
        .trim()
    );
    return /^[0-9−+\-., ]+$/.test(s);
  };

  // Parsowanie:
  //  - "\\dfrac{2}{5}" / "\\frac{2}{5}"
  //  - "1\\dfrac{1}{4}" / "-3\\frac{1}{2}"
  //  - "-\\dfrac{1}{2}" / "−\\dfrac{1}{2}"  (SAM ZNAK PRZED UŁAMKIEM)
  // Zwraca { whole, num, den } lub null.
  const parseFractionLatex = (latexRaw) => {
    if (!latexRaw) return null;

    // dopuszczamy Unicode minus "−"
    let s = String(latexRaw)
      .replace(/\\,/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // Ujednolicamy minus do ASCII na czas parsowania
    const sParse = s.replace(/−/g, "-");

    // 1) liczba mieszana: "-3 \dfrac{1}{3}"
    const mixed = sParse.match(
      /^([+\-]?\d+)\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/
    );

    let whole = null;
    let fracPart = sParse;

    if (mixed) {
      whole = mixed[1];
      fracPart = mixed[2];
    } else {
      // 2) sam znak przed ułamkiem: "-\dfrac{1}{2}" albo "+\frac{1}{2}"
      const signOnly = sParse.match(
        /^([+\-])\s*(\\(?:d)?frac\{[^}]+\}\{[^}]+\})$/
      );
      if (signOnly) {
        whole = signOnly[1]; // trzymamy sam znak jako whole
        fracPart = signOnly[2];
      }
    }

    const frac = fracPart.match(/\\(?:d)?frac\{([^}]+)\}\{([^}]+)\}/);
    if (!frac) return null;

    let num = String(frac[1]).replace(/[{}]/g, "").trim();
    let den = String(frac[2]).replace(/[{}]/g, "").trim();

    return {
      whole: whole != null ? normalizeMinus(String(whole).trim()) : null,
      num: normalizeMinus(num),
      den: normalizeMinus(den),
    };
  };

  const approxTextW = (txt, fs) => {
    const t = String(txt ?? "");
    return Math.max(0, t.length * fs * 0.58);
  };

  const FractionSvgLabel = ({ x, y, frac, fs = fontSize }) => {
    if (!frac) return null;

    const whole = frac.whole;
    const num = frac.num;
    const den = frac.den;

    const fsWhole = fs;
    const fsFrac = Math.max(11, Math.round(fs * 0.78));

    // większy odstęp, żeby minus/whole nie „dotykał” ułamka
    const gap = Math.round(fs * 0.34);
    const fracPad = Math.max(6, Math.round(fs * 0.22));

    const wholeW = whole ? approxTextW(whole, fsWhole) : 0;
    const numW = approxTextW(num, fsFrac);
    const denW = approxTextW(den, fsFrac);

    const fracW = Math.max(numW, denW) + fracPad * 2;
    const totalW = whole ? wholeW + gap + fracW : fracW;

    const startX = -totalW / 2;
    const fracXCenter = startX + (whole ? wholeW + gap + fracW / 2 : fracW / 2);

    const numY = 0;
    const lineY = fsFrac * 1.05;
    const denY = fsFrac * 1.35;

    return (
      <g transform={`translate(${x} ${y})`} style={{ pointerEvents: "none" }}>
        {whole && (
          <text
            x={startX}
            // wyśrodkowanie pionowe względem całego bloku ułamka (żeby minus nie nachodził na kreskę)
            y={Math.round((denY + fsFrac) / 2)}
            textAnchor="start"
            dominantBaseline="middle"
            fontSize={fsWhole}
            fontFamily={fontFamily}
            fill="currentColor"
          >
            {whole}
          </text>
        )}

        <text
          x={fracXCenter}
          y={numY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {num}
        </text>

        <line
          x1={fracXCenter - fracW / 2}
          y1={lineY}
          x2={fracXCenter + fracW / 2}
          y2={lineY}
          stroke="currentColor"
          strokeWidth={Math.max(1.6, Math.round(fs * 0.08))}
          strokeLinecap="round"
        />

        <text
          x={fracXCenter}
          y={denY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={fsFrac}
          fontFamily={fontFamily}
          fill="currentColor"
        >
          {den}
        </text>
      </g>
    );
  };

  /* ── punkty skończone ───────────────────────────────────────────── */
  const finitePoints = useMemo(() => {
    const ps = [];
    if (showZero) ps.push(0);
    for (const r of ranges) {
      if (Number.isFinite(r.from)) ps.push(r.from);
      if (Number.isFinite(r.to)) ps.push(r.to);
    }
    return ps;
  }, [ranges, showZero]);

  /* ── pomiar etykiet (KaTeX) ─────────────────────────────────────── */
  const approxLabelWidth = (latex) => {
    const s = String(latex || "");
    const extra = s.includes("\\dfrac") || s.includes("\\frac") ? 48 : 28;
    return Math.max(90, Math.min(240, 8 * s.length + extra));
  };
  const tallestRowH = 52;
  const labelYOffset = 12;

  const maxLbl = Math.max(
    showZero ? approxLabelWidth("0") : 0,
    ...ranges.map((r) =>
      Math.max(
        approxLabelWidth(r.leftLabelLatex ?? (Number.isFinite(r.from) ? String(r.from) : "")),
        approxLabelWidth(r.rightLabelLatex ?? (Number.isFinite(r.to) ? String(r.to) : ""))
      )
    )
  );

  /* ── geometria osi ──────────────────────────────────────────────── */
  const padSide = 24 + maxLbl / 2 + 8;
  const leftX = padSide;
  const rightX = width - padSide;
  const axisPxW = Math.max(0, rightX - leftX);

  const baseAxisFrac = layout === "stacked" ? 0.42 : 0.62;
  const axisY1 = Math.round(height * baseAxisFrac);
  const axisGap = 46;
  const axisY2 = axisY1 + axisGap;

  const pinTopY1 = axisY1 - 28;
  const pinTopY2 = axisY2 - 28;

  /* ── wykrywanie „dwóch skończonych końców” ──────────────────────── */
  const pair = useMemo(() => {
    const ends = [];
    for (const r of ranges) {
      if (Number.isFinite(r.from)) ends.push(r.from);
      if (Number.isFinite(r.to)) ends.push(r.to);
    }
    const uniq = Array.from(new Set(ends.sort((a, b) => a - b)));
    return uniq.length === 2 ? uniq : null;
  }, [ranges]);

  const hasTwoFinite = !!pair;
  const twoHalfInfinite = useMemo(() => {
    if (ranges.length !== 2) return false;
    const isHalf = (r) => (Number.isFinite(r.from) ^ Number.isFinite(r.to)) === 1;
    return isHalf(ranges[0]) && isHalf(ranges[1]);
  }, [ranges]);

  /* ── mapowanie X ────────────────────────────────────────────────── */
  let mapX;
  let xMin, xMax;
  let showZeroTick = showZero;

  const shouldAutoLock = autoLockTwoFinite && hasTwoFinite && (twoHalfInfinite || layout === "single");
  const useLock = (lockPair || shouldAutoLock) && hasTwoFinite && axisPxW > 0;

  if (useLock) {
    const [v1, v2] = pair[0] <= pair[1] ? pair : [pair[1], pair[0]];
    const [f1, f2] = lockPair ? pairFractions : twoFiniteFractions;
    const F1 = Math.max(0, Math.min(1, f1));
    const F2 = Math.max(0, Math.min(1, f2));

    const span = v2 - v1 || 1;
    const beta = ((F2 - F1) * axisPxW) / span;
    const alpha = leftX + F1 * axisPxW - beta * v1;

    mapX = (x) => {
      if (x === Infinity) return rightX;
      if (x === -Infinity) return leftX;
      return alpha + beta * x;
    };

    showZeroTick = showZero && 0 >= v1 && 0 <= v2;
    xMin = v1;
    xMax = v2;
  } else {
    const points = finitePoints.length ? finitePoints : [0];
    const absNearest = Math.min(...points.map((v) => Math.abs(v)));
    let mEff = Math.max(marginMin, Math.min(marginMax, margin));

    if (absNearest > 0 && axisPxW > 0) {
      const maxByGap = (axisPxW * absNearest / minTickGapPx - absNearest) / 2;
      const capped = isFinite(maxByGap)
        ? Math.max(marginMin, Math.min(marginMax, maxByGap))
        : marginMin;
      mEff = Math.min(mEff, capped);
    } else {
      mEff = marginMin;
    }

    const minFinite = Math.min(...points);
    const maxFinite = Math.max(...points);
    xMin = minFinite - mEff;
    xMax = maxFinite + mEff;

    mapX = (x) => {
      if (x === Infinity) return rightX;
      if (x === -Infinity) return leftX;
      return leftX + ((x - xMin) / (xMax - xMin)) * axisPxW;
    };

    showZeroTick = showZero && 0 >= xMin && 0 <= xMax;
  }

  const x0 = mapX(0);

  /* ── oś z zakresami ─────────────────────────────────────────────── */
  const AxisWithRanges = ({ axisY, pinTopY, localRanges }) => {
    const maskId = useRef(`mask-${Math.random().toString(36).slice(2)}`).current;

    // „dziury” w wypełnieniu pod otwartymi kółkami
    const holes = [];
    localRanges.forEach((r) => {
      if (Number.isFinite(r.from) && !r.leftClosed) holes.push({ x: mapX(r.from), y: pinTopY, r: pinR + 1 });
      if (Number.isFinite(r.to) && !r.rightClosed) holes.push({ x: mapX(r.to), y: pinTopY, r: pinR + 1 });
    });

    const addLabel = (latexOrNumber, xp, keyId) => {
      if (latexOrNumber == null) return null;

      const raw = String(latexOrNumber);
      const latex = normalizeMinus(raw);

      // 1) ułamek / liczba mieszana / sam znak przed ułamkiem → czyste SVG
      const frac = parseFractionLatex(latex);
      if (frac) {
        return (
          <FractionSvgLabel
            key={keyId}
            x={xp}
            y={axisY + labelYOffset + 10}
            frac={frac}
            fs={fontSize}
          />
        );
      }

      // 2) liczba prosta → <text>
      if (isSimpleNumericLatex(latex)) {
        const txt = normalizeMinus(
          String(latex ?? "")
            .replace(/\\,/g, " ")
            .replace(/[{}]/g, "")
            .replace(/\s+/g, " ")
            .trim()
        );

        return (
          <text
            key={keyId}
            x={xp}
            y={axisY + labelYOffset + 10}
            textAnchor="middle"
            dominantBaseline="hanging"
            fontSize={fontSize}
            fontFamily={fontFamily}
            fill="currentColor"
            style={{ pointerEvents: "none" }}
          >
            {txt}
          </text>
        );
      }

      // 3) złożone → foreignObject + KaTeX (rzadko)
      const w = approxLabelWidth(latex);
      const x = Math.max(0, Math.min(width - w, xp - w / 2));

      return (
        <foreignObject
          key={keyId}
          x={x}
          y={axisY + labelYOffset}
          width={w}
          height={tallestRowH}
          pointerEvents="none"
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              width: "100%",
              height: "100%",
              textAlign: "center",
            }}
          >
            <InlineMath math={latex} />
          </div>
        </foreignObject>
      );
    };

    return (
      <>
        {holes.length > 0 && (
          <defs>
            <mask id={maskId}>
              <rect x="0" y="0" width={width} height={height} fill="white" />
              {holes.map((h, i) => (
                <circle key={i} cx={h.x} cy={h.y} r={h.r} fill="black" />
              ))}
            </mask>
          </defs>
        )}

        {/* pasek tła (z maską pod otwartymi kółkami) */}
        {localRanges.map((r, idx) => {
          const xL = Number.isFinite(r.from) ? mapX(r.from) : leftX;
          const xR = Number.isFinite(r.to) ? mapX(r.to) : rightX;
          return (
            <rect
              key={`fill-${idx}`}
              x={Math.min(xL, xR)}
              y={pinTopY}
              width={Math.max(0, Math.abs(xR - xL))}
              height={axisY - pinTopY}
              fill={color}
              fillOpacity="0.5"
              stroke={color}
              strokeOpacity="0.65"
              strokeWidth="2"
              rx="6"
              {...(holes.length ? { mask: `url(#${maskId})` } : {})}
            />
          );
        })}

        {/* oś + strzałka */}
        <line x1={leftX} y1={axisY} x2={rightX} y2={axisY} stroke="currentColor" strokeWidth="2" />
        <path
          d={`M ${rightX - AR} ${axisY - AR / 2} L ${rightX} ${axisY} L ${rightX - AR} ${axisY + AR / 2} Z`}
          fill="currentColor"
        />

        {/* tick 0 — TYLKO jeżeli 0 leży w zakresie */}
        {showZeroTick && (
          <line x1={x0} y1={axisY - 6} x2={x0} y2={axisY + 6} stroke="currentColor" strokeWidth="2" />
        )}

        {/* końce + piny + poziome linie */}
        {localRanges.map((r, idx) => {
          const nodes = [];
          const hasL = Number.isFinite(r.from);
          const hasR = Number.isFinite(r.to);

          const xL = hasL ? mapX(r.from) : leftX;
          const xR = hasR ? mapX(r.to) : rightX;

          // ticki
          if (hasL)
            nodes.push(
              <line
                key={`tL-${idx}`}
                x1={xL}
                y1={axisY - 6}
                x2={xL}
                y2={axisY + 6}
                stroke="currentColor"
                strokeWidth="2"
              />
            );
          if (hasR)
            nodes.push(
              <line
                key={`tR-${idx}`}
                x1={xR}
                y1={axisY - 6}
                x2={xR}
                y2={axisY + 6}
                stroke="currentColor"
                strokeWidth="2"
              />
            );

          // pionowe piny
          if (hasL) {
            const yEnd = r.leftClosed ? pinTopY : pinTopY + pinR - halfW;
            nodes.push(
              <line key={`pinL-${idx}`} x1={xL} y1={axisY} x2={xL} y2={yEnd} stroke={color} strokeWidth={strokeW} />
            );
          }
          if (hasR) {
            const yEnd = r.rightClosed ? pinTopY : pinTopY + pinR - halfW;
            nodes.push(
              <line key={`pinR-${idx}`} x1={xR} y1={axisY} x2={xR} y2={yEnd} stroke={color} strokeWidth={strokeW} />
            );
          }

          // poziome grube linie
          if (hasL && hasR) {
            const startX = r.leftClosed ? xL : xL + pinR + halfW;
            const endX = r.rightClosed ? xR : xR - pinR - halfW;
            nodes.push(
              <line
                key={`h-mid-${idx}`}
                x1={startX}
                y1={pinTopY}
                x2={endX}
                y2={pinTopY}
                stroke={color}
                strokeWidth={strokeW}
                strokeLinecap="round"
              />
            );
          } else if (hasL && !hasR) {
            const startX = r.leftClosed ? xL : xL + pinR + halfW;
            nodes.push(
              <line
                key={`h-right-${idx}`}
                x1={startX}
                y1={pinTopY}
                x2={rightX}
                y2={pinTopY}
                stroke={color}
                strokeWidth={strokeW}
                strokeLinecap="round"
              />
            );
          } else if (!hasL && hasR) {
            const startX = r.rightClosed ? xR : xR - pinR - halfW;
            nodes.push(
              <line
                key={`h-left-${idx}`}
                x1={startX}
                y1={pinTopY}
                x2={leftX}
                y2={pinTopY}
                stroke={color}
                strokeWidth={strokeW}
                strokeLinecap="round"
              />
            );
          }

          // kółka
          if (hasL) {
            nodes.push(
              r.leftClosed ? (
                <circle key={`cL-${idx}`} cx={xL} cy={pinTopY} r={pinR} fill={color} />
              ) : (
                <circle
                  key={`cL-${idx}`}
                  cx={xL}
                  cy={pinTopY}
                  r={pinR}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeW}
                />
              )
            );
          }
          if (hasR) {
            nodes.push(
              r.rightClosed ? (
                <circle key={`cR-${idx}`} cx={xR} cy={pinTopY} r={pinR} fill={color} />
              ) : (
                <circle
                  key={`cR-${idx}`}
                  cx={xR}
                  cy={pinTopY}
                  r={pinR}
                  fill="none"
                  stroke={color}
                  strokeWidth={strokeW}
                />
              )
            );
          }

          return <React.Fragment key={`ends-${idx}`}>{nodes}</React.Fragment>;
        })}

        {/* etykiety */}
        {localRanges.map((r, idx) => {
          const nodes = [];

          if (Number.isFinite(r.from)) {
            const lbl = r.leftLabelLatex ?? String(r.from);
            nodes.push(addLabel(lbl, mapX(r.from), `lblL-${idx}`));
          }
          if (Number.isFinite(r.to)) {
            const lbl = r.rightLabelLatex ?? String(r.to);
            nodes.push(addLabel(lbl, mapX(r.to), `lblR-${idx}`));
          }

          return <React.Fragment key={`labels-${idx}`}>{nodes}</React.Fragment>;
        })}
      </>
    );
  };

  /* ── układ „stacked” (dwie osie) ─────────────────────────────────── */
  let topRanges = ranges;
  let bottomRanges = [];
  if (layout === "stacked" && ranges.length >= 2) {
    const xMinLocal = Number.isFinite(xMin) ? xMin : Math.min(...finitePoints, 0);
    const xMaxLocal = Number.isFinite(xMax) ? xMax : Math.max(...finitePoints, 0);

    const withCenter = ranges.map((r) => ({
      r,
      c: (Number.isFinite(r.from) ? r.from : xMinLocal) + (Number.isFinite(r.to) ? r.to : xMaxLocal),
    }));

    withCenter.sort((a, b) => a.c - b.c);
    topRanges = [withCenter[0].r];
    bottomRanges = [withCenter[withCenter.length - 1].r];
  }

  /* ── render ─────────────────────────────────────────────────────── */
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}>
        <AxisWithRanges
          axisY={Math.round(height * (layout === "stacked" ? 0.42 : 0.62))}
          pinTopY={layout === "stacked" ? Math.round(height * 0.42) - 28 : Math.round(height * 0.62) - 28}
          localRanges={layout === "stacked" ? topRanges : ranges}
        />
        {layout === "stacked" && (
          <AxisWithRanges
            axisY={Math.round(height * 0.42) + 46}
            pinTopY={Math.round(height * 0.42) + 46 - 28}
            localRanges={bottomRanges}
          />
        )}
      </svg>
    </div>
  );
}
